// Event search shared by the Search and Calendar pages (support #279162).
//
// Matches what the user typed against the event's name, organiser, venue,
// address (where the town/city lives — there is no separate city column),
// postcode, category and description, and grades each match by WHERE it was found:
//   1 LOCATION  the place is in the venue / address / postcode (or the state matches)
//   2 NAME      the text is in the event name
//   3 ORGANISER_CATEGORY  the text is the organiser or the category
//   4 KEYWORD   every word appears somewhere, including the description
// If the text is a place (it appears in some event's venue/address/postcode),
// only LOCATION and NAME matches count, so "London" returns events in London
// rather than events elsewhere that mention London in their description.
// States/provinces are stored as abbreviations inside the address ("Austin, TX",
// "Campbelltown NSW 2560"), so they are resolved to name + abbreviation first.
// Pure functions over the already-loaded events — no extra fetches.

const US = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon",
  PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};
const AU = {
  NSW: "New South Wales", VIC: "Victoria", QLD: "Queensland", SA: "South Australia",
  WA: "Western Australia", TAS: "Tasmania", NT: "Northern Territory",
  ACT: "Australian Capital Territory",
};
const CA = {
  AB: "Alberta", BC: "British Columbia", MB: "Manitoba", NB: "New Brunswick",
  NL: "Newfoundland and Labrador", NS: "Nova Scotia", NT: "Northwest Territories",
  NU: "Nunavut", ON: "Ontario", PE: "Prince Edward Island", QC: "Quebec",
  SK: "Saskatchewan", YT: "Yukon",
};
const REGIONS = { US, AU, CA };

// Connective words people type ("track days in texas") that must not be required
// to appear in the event text.
const STOPWORDS = new Set(["in", "at", "near", "the", "and", "of", "event", "events", "&"]);

export function normalize(s) {
  return (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Find a state/province in the query for the selected country. Full names match
// anywhere (longest first, so "West Virginia" beats "Virginia"). A bare
// abbreviation only counts when it IS the whole query ("TX") or was typed in
// capitals ("Cars and Coffee TX") — so words like "in", "or", "me" are never
// mistaken for Indiana/Oregon/Maine.
export function findRegion(rawQuery, country) {
  const table = REGIONS[country];
  if (!table) return null;
  const q = ` ${normalize(rawQuery)} `;
  const byName = Object.entries(table).sort((a, b) => b[1].length - a[1].length);
  for (const [abbr, name] of byName) {
    const n = normalize(name);
    if (q.includes(` ${n} `)) return { abbr, name, consumed: n };
  }
  const words = (rawQuery || "").trim().split(/[\s,]+/).filter(Boolean);
  for (const w of words) {
    const up = w.replace(/\./g, "").toUpperCase();
    if (!table[up]) continue;
    const isWholeQuery = words.length === 1;
    const typedAsAbbr = w.replace(/\./g, "") === up;
    if (isWholeQuery || typedAsAbbr) return { abbr: up, name: table[up], consumed: normalize(w) };
  }
  return null;
}

// Does the event's address place it in the region? The abbreviation must stand
// alone (", TX," / " TX 78219" / "Campbelltown NSW 2560"), case-sensitive, so
// "Texas Motor Speedway" in a venue name alone is not treated as a location.
function inRegion(ev, region, country) {
  const addr = ev.address || "";
  const abbrRe = new RegExp(`(^|[\\s,])${escapeRe(region.abbr)}(?=$|[\\s,.\\d])`);
  if (abbrRe.test(addr)) return true;
  const name = normalize(region.name);
  let a = ` ${normalize(addr)} `;
  // "Virginia" must not match "West Virginia": blank out longer region names that
  // contain this one before looking for it.
  Object.values(REGIONS[country] || {}).forEach((other) => {
    const o = normalize(other);
    if (o !== name && o.includes(name)) a = a.split(` ${o} `).join("  ");
  });
  return a.includes(` ${name} `);
}

// Terms left to text-match once the region (if any) is taken out of the query.
function remainingTerms(rawQuery, region) {
  let q = ` ${normalize(rawQuery)} `;
  if (region) q = q.replace(` ${region.consumed} `, " ");
  return q.split(" ").filter((t) => t && !STOPWORDS.has(t));
}

export const TIER = { LOCATION: 1, NAME: 2, ORGANISER_CATEGORY: 3, KEYWORD: 4, NEARBY: 5 };

// Whole words / whole phrase ("london" matches "London SE1", not "Londonderry").
const hasWords = (field, phrase) => ` ${field} `.includes(` ${phrase} `);
// Word starts with the text ("car" matches "cars", "track day" matches "track days").
const hasPrefix = (field, text) => ` ${field}`.includes(` ${text}`);

const fieldCache = new WeakMap();
function fieldsOf(ev) {
  let f = fieldCache.get(ev);
  if (!f) {
    f = {
      name: normalize(ev.name),
      organiser: normalize(ev.organiser_name),
      location: normalize([ev.venue_name, ev.address, ev.postcode].join(" ")),
      category: normalize(ev.category),
      description: normalize(ev.description),
    };
    f.all = [f.name, f.organiser, f.location, f.category, f.description].join(" ");
    fieldCache.set(ev, f);
  }
  return f;
}

// Street names reuse place names ("London Road" in Kent), so a place followed by
// a street word is not treated as the event's location.
const STREET = "(?:road|rd|street|st|lane|ln|avenue|ave|way|drive|dr|close|crescent|terrace|parade|row|highway|hwy)";

// Location match: whole words, or a word prefix when the text has digits
// (partial postcodes like "SW1" or "2560").
function inLocation(f, phrase) {
  const loc = ` ${f.location} `.replace(new RegExp(` ${escapeRe(phrase)} ${STREET}(?= )`, "g"), " ");
  return loc.includes(` ${phrase} `) || (/\d/.test(phrase) && ` ${f.location}`.includes(` ${phrase}`));
}

// Search `events` (already scoped to the selected country) for `rawQuery`.
// Returns { region, regionOnly, placeQuery, hits: [{ ev, tier }] }.
export function searchEvents(events, rawQuery, country) {
  const region = findRegion(rawQuery, country);
  const terms = remainingTerms(rawQuery, region);
  const phrase = terms.join(" ");
  let hits = [];
  for (const ev of events) {
    if (region && !inRegion(ev, region, country)) continue;
    if (!terms.length) {
      hits.push({ ev, tier: TIER.LOCATION });
      continue;
    }
    const f = fieldsOf(ev);
    let tier = 0;
    if (inLocation(f, phrase)) tier = TIER.LOCATION;
    // An exact category ("Motorsport", "Track Days") ranks with name matches, so a
    // category search isn't led by events that merely mention the word in their title.
    else if (hasPrefix(f.name, phrase) || f.category === phrase || f.category === `${phrase}s`) tier = TIER.NAME;
    else if (hasPrefix(f.organiser, phrase) || hasPrefix(f.category, phrase)) tier = TIER.ORGANISER_CATEGORY;
    else if (terms.every((t) => hasPrefix(f.all, t))) tier = TIER.KEYWORD;
    if (tier) hits.push({ ev, tier });
  }
  // The text reads as a PLACE when location matches are substantial compared with
  // the weak matches (organiser / category / description): "London" (37 located
  // there vs 42 mentions) is a place; "Cars and Coffee" (2 venues vs 81 mentions)
  // is not. For a place, events that only mention it in the organiser, category
  // or description are dropped, so "London" returns events actually in London.
  const locationHits = hits.filter((h) => h.tier === TIER.LOCATION).length;
  const weakHits = hits.filter((h) => h.tier >= TIER.ORGANISER_CATEGORY).length;
  const placeQuery = !region && terms.length > 0 && locationHits > 0 && locationHits * 4 >= weakHits;
  if (placeQuery) hits = hits.filter((h) => h.tier <= TIER.NAME);
  return { region, regionOnly: !!region && !terms.length, placeQuery, hits };
}

// Ranking for lists: best match first, then soonest current/next occurrence.
export function byTierThenSoonest(a, b) {
  if (a.tier !== b.tier) return a.tier - b.tier;
  const ta = a.ev.start_iso ? new Date(a.ev.start_iso).getTime() : Infinity;
  const tb = b.ev.start_iso ? new Date(b.ev.start_iso).getTime() : Infinity;
  return ta - tb;
}

// ---------------------------------------------------------------------------
// Geographic + event-type search (support #279162). Layered on top of
// searchEvents: the written-address matching above is unchanged and still
// contributes hits; this adds
//   - the searched place's real boundary (county / state / city, from
//     /api/geoboundary) tested against each event's MAP PIN, so "Hertfordshire"
//     finds every event located there even if the word is not in the listing;
//   - event-type words ("car meets", "track days", "f1") mapped to categories, so a
//     combined query like "car meets in Hertfordshire" means type + place;
//   - common abbreviations ("herts") and minor typos when nothing else matches.
// ---------------------------------------------------------------------------

const PLACE_ALIASES = {
  herts: "hertfordshire", beds: "bedfordshire", bucks: "buckinghamshire", hants: "hampshire",
  lancs: "lancashire", notts: "nottinghamshire", oxon: "oxfordshire", northants: "northamptonshire",
  staffs: "staffordshire", wilts: "wiltshire", glos: "gloucestershire", leics: "leicestershire",
  lincs: "lincolnshire", cambs: "cambridgeshire", yorks: "yorkshire", nyc: "new york city",
  sf: "san francisco",
};

// Phrases are matched on the normalised query (lower-case words, single spaces),
// longest first within a rule and most specific rule first.
const TYPE_RULES = [
  { phrases: ["motorcycle meets", "motorcycle meet", "motorbike meets", "motorbike meet", "bike meets", "bike meet", "biker meets", "biker meet"], cats: ["Motorcycles"], kw: ["motorcycle", "motorbike", "biker", "bike"] },
  { phrases: ["motorcycles", "motorcycle", "motorbikes", "motorbike", "bikes", "bike", "bikers", "biker"], cats: ["Motorcycles"] },
  { phrases: ["cars and coffee", "car and coffee", "cars coffee", "car coffee", "coffee mornings", "coffee morning", "coffee meets", "coffee meet", "coffee"], cats: ["Meets & Coffee"] },
  { phrases: ["car meets", "car meet", "meet ups", "meet up", "meetups", "meetup", "meets", "meet", "cruise ins", "cruise in", "cruises", "cruise"], cats: ["Meets & Coffee"] },
  { phrases: ["track days", "track day", "trackdays", "trackday", "circuit days", "circuit day"], cats: ["Track Days"] },
  { phrases: ["car shows", "car show", "auto shows", "auto show", "shows", "show", "concours", "expos", "expo"], cats: ["Shows"] },
  // Specific disciplines match events that mention them (not every Motorsport event).
  { phrases: ["formula 1", "formula one", "f1", "grand prix"], cats: ["Motorsport"], kw: ["formula 1", "formula one", "f1", "grand prix"], kwOnly: true },
  { phrases: ["rallies", "rally"], cats: ["Motorsport"], kw: ["rally", "rallies"], kwOnly: true },
  { phrases: ["drifting", "drift"], cats: ["Motorsport"], kw: ["drift"], kwOnly: true },
  { phrases: ["motorsports", "motorsport", "motor sports", "motor sport", "drag racing", "racing", "races", "race"], cats: ["Motorsport"] },
  { phrases: ["classic cars", "classic car", "classics", "classic", "vintage"], cats: ["Cars", "Shows", "Meets & Coffee"], kw: ["classic", "vintage"], kwOnly: true },
  { phrases: ["cars", "car"], cats: ["Cars"] },
];

const QUERY_FILLER = new Set(["in", "at", "near", "the", "and", "of", "event", "events", "around", "within", "for", "with", "to", "from", "a", "an", "&"]);

// Split a query into event-type rules and the remaining place text.
export function parseQuery(rawQuery) {
  let q = ` ${normalize(rawQuery)} `;
  const types = [];
  for (const rule of TYPE_RULES) {
    let hit = false;
    for (const p of rule.phrases) {
      const needle = ` ${p} `;
      if (q.includes(needle)) {
        q = q.split(needle).join(" ");
        hit = true;
      }
    }
    if (hit) types.push(rule);
  }
  const place = q
    .split(" ")
    .filter((t) => t && !QUERY_FILLER.has(t))
    .map((t) => PLACE_ALIASES[t] || t)
    .join(" ");
  return { types, place };
}

// Predicate for the matched event types, or null when the query has none.
export function typePredicate(types) {
  if (!types.length) return null;
  return (ev) => {
    const f = fieldsOf(ev);
    return types.some((r) =>
      r.kwOnly
        ? r.kw.some((k) => hasPrefix(f.all, k))
        : r.cats.includes(ev.category) || (r.kw && r.kw.some((k) => hasPrefix(f.all, k)))
    );
  };
}

// Ray casting over one ring of [lng, lat] points.
function ringHas(ring, lng, lat) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

// Is the map pin inside the boundary returned by /api/geoboundary?
export function inBoundary(boundary, lat, lng) {
  if (!boundary || typeof lat !== "number" || typeof lng !== "number") return false;
  const [s, n, w, e] = boundary.bbox;
  if (lat < s || lat > n || lng < w || lng > e) return false;
  return boundary.polygons.some(
    (poly) => ringHas(poly[0], lng, lat) && !poly.slice(1).some((hole) => ringHas(hole, lng, lat))
  );
}

// Levenshtein distance within `max`, with early exit.
function editWithin(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return false;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > max) return false;
    prev = cur;
  }
  return prev[b.length] <= max;
}

const fuzzyWordsCache = new WeakMap();
function fuzzyWords(ev) {
  let w = fuzzyWordsCache.get(ev);
  if (!w) {
    const f = fieldsOf(ev);
    w = new Set([f.name, f.organiser, f.location, f.category, f.description].join(" ").split(" ").filter((x) => x.length >= 4));
    fuzzyWordsCache.set(ev, w);
  }
  return w;
}

// Typo tolerance, used only when nothing else matched: every query word (4+ letters)
// must be within 1-2 edits of a word in the event's name, organiser, place or category.
function fuzzyHits(events, rawQuery) {
  const terms = normalize(rawQuery).split(" ").filter((t) => t.length >= 4 && !QUERY_FILLER.has(t));
  if (!terms.length) return [];
  const hits = [];
  for (const ev of events) {
    const words = fuzzyWords(ev);
    const ok = terms.every((t) => {
      const max = t.length >= 6 ? 2 : 1;
      for (const w of words) if (editWithin(t, w, max)) return true;
      return false;
    });
    if (ok) hits.push({ ev, tier: TIER.KEYWORD });
  }
  return hits;
}

// searchEvents + boundary / event-type / typo handling. `boundary` is the
// /api/geoboundary result for parseQuery(rawQuery).place (or null).
export function searchEventsGeo(events, rawQuery, country, boundary) {
  const base = searchEvents(events, rawQuery, country);
  const { types, place } = parseQuery(rawQuery);
  const typeOk = typePredicate(types);
  const byId = new Map();
  const add = (ev, tier) => {
    const k = ev.id ?? ev;
    const cur = byId.get(k);
    if (!cur || tier < cur.tier) byId.set(k, { ev, tier });
  };
  if (boundary && place) {
    for (const ev of events) {
      if (inBoundary(boundary, ev.latitude, ev.longitude) && (!typeOk || typeOk(ev))) add(ev, TIER.LOCATION);
    }
    if (!typeOk) base.hits.forEach((h) => add(h.ev, h.tier));
  } else if (typeOk && !place) {
    for (const ev of events) if (typeOk(ev)) add(ev, TIER.NAME);
    base.hits.forEach((h) => add(h.ev, h.tier));
  } else {
    base.hits.forEach((h) => add(h.ev, h.tier));
  }
  let hits = [...byId.values()];
  if (!hits.length) hits = fuzzyHits(events, rawQuery);
  return { ...base, hits, types, place };
}
